import { Button, Typography } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { SuraasaPlusSubscriptionStatus } from "api/resources/suraasaPlus/types"
import clsx from "clsx"
import FullPageLoading from "components/FullPageLoading"
import { format } from "date-fns"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { ArrowLeft, PageFlip } from "iconoir-react"
import { Fragment } from "react/jsx-runtime"
import { Link } from "react-router-dom"
import routes from "routes"
import { downloadInvoice } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import UpdatePaymentBadge from "./Badges/UpdatePaymentBadge"
import {
  getPaymentDetailsObject,
  getPaymentMethodString,
  getPlanString,
  getTimeSpan,
} from "./utils"

const PaymentHistory = () => {
  const { subscriptionDetailsQuery } = useSuraasaPlusContext()
  const isCancelled =
    subscriptionDetailsQuery?.data?.subscription.status ===
    SuraasaPlusSubscriptionStatus.cancelled
  const formatPrice = useFormatPrice()

  const renewalPaymentOverdue =
    subscriptionDetailsQuery?.data?.subscription?.status ===
    SuraasaPlusSubscriptionStatus.paymentOverdue

  const invoiceMutation = useMutation({
    mutationFn: (transactionId: string) =>
      api.orders.getInvoice({ urlParams: { transactionId } }),
    onSuccess: data => {
      downloadInvoice(data)
    },
  })

  const { transactions, product, subscription } =
    subscriptionDetailsQuery?.data ?? {}

  if (subscriptionDetailsQuery?.isLoading) return <FullPageLoading />

  return (
    <div>
      <Link to={routes.suraasaPlusSettings}>
        <Button
          variant="text"
          startAdornment={<ArrowLeft className="size-2.5" strokeWidth={2} />}
        >
          Back
        </Button>
      </Link>
      <div className="mb-2 mt-3 sm:mb-3">
        <Typography variant="title2" className="mb-2 text-onSurface-800">
          Payment History
        </Typography>
        <div className="mt-1.5 rounded-lg border border-onSurface-200 p-2">
          <Typography
            variant="title4"
            className={clsx("mb-1 text-onSurface-800", {
              "mb-2.5": renewalPaymentOverdue,
            })}
          >
            {getPlanString(product?.subscriptionDetails?.intervalType)}
          </Typography>
          {renewalPaymentOverdue ? (
            <UpdatePaymentBadge />
          ) : subscription?.validThrough ? (
            <Typography variant="smallBody" className="text-onSurface-800">
              {!isCancelled ? "Your next billing date is" : "Cancels on"}{" "}
              <span className="text-strongSmallBody">
                {format(new Date(subscription.validThrough), "d MMMM yyyy")}
              </span>
            </Typography>
          ) : (
            <></>
          )}
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-2 md:block">
        {/* Desktop View */}
        <div className="hidden grid-cols-[15%_35%_25%_25%] md:grid">
          <Typography variant="smallBody" className="text-muted">
            Date
          </Typography>
          <Typography variant="smallBody" className="text-muted">
            Description
          </Typography>
          <Typography variant="smallBody" className="text-muted">
            Method
          </Typography>
          <Typography variant="smallBody" className="text-muted">
            Total
          </Typography>
        </div>

        {transactions &&
          transactions.map((transaction, idx) => {
            const method = transaction.paymentDetails?.method

            const { identifier, issuer, network } = getPaymentDetailsObject(
              transaction.paymentDetails
            )

            const { label, icon } = getPaymentMethodString({
              method: method,
              identifier: identifier,
              issuer: issuer,
              network: network,
            })

            return (
              <Fragment key={transaction.id}>
                {/* Desktop View */}
                <div
                  className={clsx(
                    "hidden grid-cols-[15%_35%_25%_25%] items-center border-b border-onSurface-200 py-2.5 md:grid",
                    { "border-none": idx === transactions.length - 1 }
                  )}
                >
                  <Typography
                    variant="body"
                    className="pr-1 text-onSurface-800"
                  >
                    {format(new Date(transaction.createdAt), "d MMM, yy")}
                  </Typography>
                  <Typography
                    variant="body"
                    className="pr-1 text-onSurface-800"
                  >
                    Suraasa Plus{" "}
                    {getTimeSpan({
                      startDate: transaction.createdAt,
                      intervalType: product?.subscriptionDetails?.intervalType,
                    })}
                  </Typography>
                  <div className="flex items-center gap-1 pr-1">
                    <img
                      src={icon}
                      alt=""
                      className="size-3.5 object-contain"
                    />
                    <Typography
                      variant="strongSmallBody"
                      className="text-muted"
                    >
                      {label}
                    </Typography>
                  </div>
                  <div className="flex items-center justify-between">
                    <Typography
                      variant="body"
                      className="shrink-0 text-onSurface-800"
                    >
                      {transaction.currency.symbol +
                        formatPrice(Number(transaction.amount))}
                    </Typography>
                    <Button
                      variant="text"
                      endAdornment={<PageFlip className="!size-2.5" />}
                      className="text-primary-500"
                      onClick={() => invoiceMutation.mutate(transaction.id)}
                      loading={invoiceMutation.isLoading}
                    >
                      Invoice
                    </Button>
                  </div>
                </div>

                {/* Mobile View */}
                <div className="rounded-lg border border-onSurface-200 bg-surface-50 p-2 md:hidden">
                  <Typography
                    variant="title4"
                    className="mb-0.5 text-onSurface-800"
                  >
                    Suraasa Plus{" "}
                    {getTimeSpan({
                      startDate: transaction.createdAt,
                      intervalType: transaction.intervalType,
                    })}
                  </Typography>
                  <Typography
                    variant="smallBody"
                    className="text-onSurface-800"
                  >
                    {format(new Date(transaction.createdAt), "d MMM, yy")}
                  </Typography>
                  <div className="my-1 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <img
                        src={icon}
                        alt=""
                        className="size-3.5 object-contain"
                      />
                      <Typography
                        variant="strongSmallBody"
                        className="text-muted"
                      >
                        {label}
                      </Typography>
                    </div>
                    <Typography variant="body" className="text-onSurface-800">
                      {transaction.currency.symbol +
                        formatPrice(Number(transaction.amount))}
                    </Typography>
                  </div>
                  <div className="border-t border-onSurface-200 pt-1">
                    <Button
                      variant="text"
                      endAdornment={<PageFlip className="!size-2.5" />}
                      className="text-primary-500"
                      onClick={() => invoiceMutation.mutate(transaction.id)}
                      loading={invoiceMutation.isLoading}
                    >
                      Invoice
                    </Button>
                  </div>
                </div>
              </Fragment>
            )
          })}
      </div>
    </div>
  )
}

export default PaymentHistory
